import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { wordApi } from "@/api/word.api";
import { useToast } from "@/hooks/use-toast";
import { useApp } from "@/hooks/use-app-context";
import { useAdminScope } from "@/hooks/use-admin-scope";
import { EXCEL_FILES_QUERY_KEY } from "./excel-files.constants";
import {
  countExcelImports,
  filterExcelImports,
  type ImportStatusFilter,
} from "./excel-file-monitor";

const PAGE_SIZE = 10;

export function useExcelFiles() {
  const { t } = useApp();
  const { canAccessLanguage, isSuperAdmin } = useAdminScope();
  const { toast } = useToast();
  const adminT = t.admin;
  const queryClient = useQueryClient();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [updatingFileId, setUpdatingFileId] = useState<number | null>(null);
  const [selectedLang, setSelectedLang] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<ImportStatusFilter>("all");
  const [page, setPage] = useState(1);

  const { data: files = [], isLoading, isError, isFetching } = useQuery({
    queryKey: EXCEL_FILES_QUERY_KEY,
    queryFn: wordApi.getInitialDataFiles,
  });

  const scopedFiles = useMemo(
    () => files.filter((file) => canAccessLanguage(file.lang)),
    [canAccessLanguage, files],
  );

  const filteredFiles = useMemo(
    () => filterExcelImports(scopedFiles, selectedLang, selectedStatus),
    [scopedFiles, selectedLang, selectedStatus],
  );
  const counts = useMemo(() => countExcelImports(scopedFiles), [scopedFiles]);

  const total = filteredFiles.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const paginatedFiles = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredFiles.slice(start, start + PAGE_SIZE);
  }, [filteredFiles, page]);

  const uniqueLangs = useMemo(() => Array.from(new Set(scopedFiles.map((file) => file.lang))), [scopedFiles]);

  useEffect(() => {
    if (isSuperAdmin || selectedLang === "all") return;
    if (!uniqueLangs.includes(selectedLang)) {
      setSelectedLang(uniqueLangs[0] ?? "all");
    }
  }, [isSuperAdmin, selectedLang, uniqueLangs]);

  useEffect(() => {
    setPage(1);
  }, [selectedLang, selectedStatus]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const downloadMutation = useMutation({
    mutationFn: async (id: number) => {
      const download = await wordApi.downloadFile(id);
      const url = window.URL.createObjectURL(download.blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", download.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    onError: () => {
      toast({
        title: t.error,
        description: adminT.excelFiles.downloadError,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }) => {
      return wordApi.updateFile(id, file);
    },
    onSuccess: () => {
      toast({
        title: t.success,
        description: adminT.excelFiles.updateSuccess,
      });
      setIsUpdateDialogOpen(false);
      setUpdatingFileId(null);
      setSelectedFile(null);
      queryClient.invalidateQueries({ queryKey: EXCEL_FILES_QUERY_KEY });
    },
    onError: () => {
      toast({
        title: t.error,
        description: adminT.excelFiles.updateError,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: wordApi.deleteFile,
    onSuccess: () => {
      toast({
        title: t.success,
        description: adminT.excelFiles.deleteSuccess,
      });
      queryClient.invalidateQueries({ queryKey: EXCEL_FILES_QUERY_KEY });
    },
    onError: () => {
      toast({
        title: t.error,
        description: adminT.excelFiles.deleteError,
        variant: "destructive",
      });
    },
  });

  const approveMutation = useMutation({
    mutationFn: wordApi.approveFile,
    onSuccess: () => {
      toast({ title: t.success, description: adminT.excelFiles.approveSuccess });
      queryClient.invalidateQueries({ queryKey: EXCEL_FILES_QUERY_KEY });
    },
    onError: () => toast({ title: t.error, description: adminT.excelFiles.approveError, variant: "destructive" }),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => wordApi.rejectFile(id, reason),
    onSuccess: () => {
      toast({ title: t.success, description: adminT.excelFiles.rejectSuccess });
      queryClient.invalidateQueries({ queryKey: EXCEL_FILES_QUERY_KEY });
    },
    onError: () => toast({ title: t.error, description: adminT.excelFiles.rejectError, variant: "destructive" }),
  });
  const deleteAllByLangMutation = useMutation({
    mutationFn: wordApi.deleteAllEXcelFilesByLang,
    onSuccess: () => {
      toast({
        title: t.success,
        description: adminT.excelFiles.deleteAllSuccess.replace("{lang}", selectedLang),
      });
      queryClient.invalidateQueries({ queryKey: EXCEL_FILES_QUERY_KEY });
    },
    onError: () => {
      toast({
        title: t.error,
        description: adminT.excelFiles.deleteAllError,
        variant: "destructive",
      });
    },
  });

  const handleUpdateSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (updatingFileId && selectedFile) {
        updateMutation.mutate({ id: updatingFileId, file: selectedFile });
      }
    },
    [updatingFileId, selectedFile, updateMutation],
  );

  const openUpdateDialog = useCallback((fileId: number) => {
    setUpdatingFileId(fileId);
    setIsUpdateDialogOpen(true);
  }, []);

  const closeUpdateDialog = useCallback(() => {
    setIsUpdateDialogOpen(false);
    setUpdatingFileId(null);
    setSelectedFile(null);
  }, []);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: EXCEL_FILES_QUERY_KEY });
  }, [queryClient]);

  return {
    t,
    adminT,
    files: paginatedFiles,
    allFiles: filteredFiles,
    isLoading,
    isError,
    isRefreshing: isFetching,
    isSuperAdmin,
    selectedFile,
    setSelectedFile,
    isUpdateDialogOpen,
    updatingFileId,
    selectedLang,
    setSelectedLang,
    selectedStatus,
    setSelectedStatus,
    uniqueLangs,
    counts,
    page,
    setPage,
    total,
    totalPages,
    downloadMutation,
    updateMutation,
    deleteMutation,
    deleteAllByLangMutation,
    approveMutation,
    rejectMutation,
    handleUpdateSubmit,
    openUpdateDialog,
    closeUpdateDialog,
    refresh,
  };
}
